import React, { useCallback } from 'react';
import { getActivityPlugin } from '../../activities/registry';
import { useRoomContext } from '../client/RoomContext';
import type { ActivityAction } from '../types/activity.types';
import type { GameMode } from '../types/gameMode.types';
import type { ParticipantRole } from '../types/room.types';
import { sandboxLogic } from '../../activities/sandbox/sandbox.logic';
import { buildPluginContext } from '../../logic/pluginRunner';

interface ActivityStageProps {
  role: ParticipantRole;
  readOnly?: boolean;
}

export function ActivityStage({ role, readOnly }: ActivityStageProps) {
  const { state, dispatchActivityAction, submitStudentInput } = useRoomContext();
  const gameMode = state.state?.core.activity.mode;
  const payload = state.state?.payload;
  const runtime = state.state?.runtime;

  const dispatch = useCallback(
    (action: ActivityAction) => {
      if (role === 'host') {
        void dispatchActivityAction(action);
        return;
      }
      if (role === 'student') {
        void submitStudentInput(action.type, action.payload);
      }
    },
    [role, dispatchActivityAction, submitStudentInput],
  );

  if (!gameMode || !payload || !runtime) {
    return (
      <div className="rounded-xl border border-dashed border-slate-600 p-6 text-center text-slate-400">
        No hay actividad cargada
      </div>
    );
  }

  const plugin = getActivityPlugin(gameMode);
  const participant = state.participants.find((p) => p.participantId === state.participantId);
  const team = state.state.core.routing.teams.find((t) => t.teamId === participant?.teamId);

  let projection: unknown;
  if (gameMode === 'sandbox' && plugin.mode === 'sandbox') {
    try {
      projection = sandboxLogic.getProjection(
        buildPluginContext(
          'sandbox',
          payload as never,
          runtime as never,
          state.state.core.routing.mode,
          state.state.core.phase,
        ),
      );
    } catch {
      projection = undefined;
    }
  }

  const viewProps = {
    mode: gameMode as GameMode,
    payload: payload as never,
    runtime: runtime as never,
    role,
    participantId: state.participantId ?? undefined,
    team,
    dispatch,
    readOnly,
    phase: state.state.core.phase,
    projection,
  };

  if (readOnly && plugin.ProjectorView) {
    return <plugin.ProjectorView {...viewProps} />;
  }
  if (role === 'host') {
    return <plugin.HostView {...viewProps} />;
  }
  return <plugin.StudentView {...viewProps} />;
}
